import type { DB } from "./types";

function defaultDocumentFormats(today: string) {
  const types = ["Quotation", "Proforma Invoice", "Sales Order", "Purchase Order", "Job Card", "Sales Invoice", "Tax Invoice", "QC Test Report", "Inspection Certificate"];
  const terms: Record<string, string[]> = {
    Quotation: ["Prices are Ex-Works.", "GST Extra as Applicable.", "Delivery within 4 Weeks.", "Payment 100% Advance."],
    "Purchase Order": ["Material should be as per specification.", "Delivery within committed date.", "Test certificate mandatory."],
    "Proforma Invoice": ["Payment as per agreed terms.", "Material dispatch after payment confirmation."],
    "Sales Order": ["Order is subject to approved technical specifications.", "Delivery schedule to be mutually agreed."],
    "Sales Invoice": ["Goods received in good condition.", "Customer acknowledgement required."],
    "Job Card": ["Production must follow approved BOM.", "Material issue only against authorized job card."],
    "Tax Invoice": ["Subject to Jaipur jurisdiction.", "Payment due as per invoice terms."],
    "QC Test Report": ["Report valid for tested serial numbers only.", "All tests performed as per applicable standards."],
    "Inspection Certificate": ["Certificate issued after QC verification.", "Dispatch subject to final approval."],
  };
  return types.map(type => ({
    id: `df-${type.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
    documentType: type,
    formatName: `${type} Standard Format`,
    active: true,
    companyName: "AMREST ELECTRICALS LIMITED",
    address: "Plot No. 42, Industrial Area, Jaipur, Rajasthan 302013",
    gstNo: "08AAGCA1234L1ZP",
    contactDetails: "info@amrest.in | +91 98765 43210",
    headerContent: "Registered manufacturer of transformer and electrical equipment.",
    footerContent: "This is a computer generated document.",
    terms: (terms[type] || ["Terms as mutually agreed."]).map((text, index) => ({ id: `${type}-${index}`, text, active: true, order: index + 1 })),
    bankDetails: "Bank: HDFC Bank\nA/C Name: AMREST ELECTRICALS LIMITED\nA/C No: 000000000000\nIFSC: HDFC0000000",
    declaration: "We declare that the information stated in this document is true and correct.",
    signatureName: "Authorized Signatory",
    qrCode: true,
    watermark: "AMREST",
    fitToSinglePage: false,
    pageSize: "A4" as const,
    orientation: "Portrait" as const,
    createdAt: today,
    updatedAt: today,
  }));
}

export function seedDB(): DB {
  const today = new Date().toISOString();
  return {
    users: [
      { id: "u-admin", name: "Admin User", email: "admin@amrest.in", username: "admin", password: "admin123", role: "admin", active: true, createdAt: today },
      { id: "u-sales1", name: "Rohit Sharma", email: "rohit@amrest.in", username: "rohit", password: "sales123", role: "sales", active: true, createdAt: today },
      { id: "u-sales2", name: "Priya Mehta", email: "priya@amrest.in", username: "priya", password: "sales123", role: "sales", active: true, createdAt: today },
      { id: "u-production", name: "Vikram Production", email: "production@amrest.in", username: "production", password: "prod123", role: "production", active: true, createdAt: today },
      { id: "u-purchase", name: "Neha Purchase", email: "purchase@amrest.in", username: "purchase", password: "purchase123", role: "purchase", active: true, createdAt: today },
      { id: "u-testing", name: "Amit Testing", email: "testing@amrest.in", username: "testing", password: "test123", role: "testing", active: true, createdAt: today },
      { id: "u-store", name: "Sanjay Store", email: "store@amrest.in", username: "store", password: "store123", role: "store", active: true, createdAt: today },
    ],
    parties: [
      { id: "p1", name: "Rajasthan State Power Ltd.", type: "customer", gst: "08AABCR1234A1Z1", address: "Jaipur, Rajasthan", contactPerson: "Mr. Verma", mobile: "9876543210", email: "purchase@rspl.in", paymentTerms: "30 days", creditLimit: 1000000, ownerId: "u-sales1", createdAt: today },
      { id: "p2", name: "Gujarat Industries Pvt Ltd", type: "customer", gst: "24AAACG5678B1Z2", address: "Ahmedabad, Gujarat", contactPerson: "Mr. Patel", mobile: "9988776655", email: "po@gujind.com", paymentTerms: "45 days", creditLimit: 500000, ownerId: "u-sales2", createdAt: today },
      { id: "p3", name: "Steelmart Suppliers", type: "vendor", gst: "27AAACS1111C1Z3", address: "Mumbai, MH", contactPerson: "Mr. Khan", mobile: "9090909090", email: "sales@steelmart.in", paymentTerms: "15 days", creditLimit: 200000, ownerId: "u-admin", createdAt: today },
      { id: "p4", name: "Copper World", type: "supplier", gst: "27AAACC2222D1Z4", address: "Pune, MH", contactPerson: "Ms. Iyer", mobile: "9123456789", email: "supply@copperworld.in", paymentTerms: "Advance", creditLimit: 0, ownerId: "u-admin", createdAt: today },
    ],
    items: [
      { id: "i1", code: "RM-CRGO", name: "CRGO Steel", category: "Raw Material", unit: "Kg", hsn: "7225", gstRate: 18, openingStock: 5000, currentStock: 4200, minStock: 500, reorderLevel: 1000, purchaseRate: 200, saleRate: 220 },
      { id: "i2", code: "RM-LVCOND", name: "LV Conductor", category: "Raw Material", unit: "Kg", hsn: "7408", gstRate: 18, openingStock: 1200, currentStock: 950, minStock: 200, reorderLevel: 400, purchaseRate: 280, saleRate: 310 },
      { id: "i3", code: "RM-HVCOND", name: "HV Conductor", category: "Raw Material", unit: "Kg", hsn: "7408", gstRate: 18, openingStock: 1100, currentStock: 800, minStock: 200, reorderLevel: 400, purchaseRate: 280, saleRate: 310 },
      { id: "i4", code: "RM-OIL", name: "Transformer Oil", category: "Raw Material", unit: "Ltr", hsn: "2710", gstRate: 18, openingStock: 3000, currentStock: 2400, minStock: 500, reorderLevel: 1000, purchaseRate: 75, saleRate: 90 },
      { id: "i5", code: "RM-TANK", name: "MS Tank", category: "Semi-Finished", unit: "Kg", hsn: "7308", gstRate: 18, openingStock: 800, currentStock: 600, minStock: 200, reorderLevel: 300, purchaseRate: 75, saleRate: 95 },
      { id: "i6", code: "RM-RAD", name: "PSR Radiator", category: "Raw Material", unit: "Nos", hsn: "8504", gstRate: 18, openingStock: 100, currentStock: 65, minStock: 20, reorderLevel: 40, purchaseRate: 110, saleRate: 130 },
      { id: "i7", code: "RM-CHANNEL", name: "Core Channel", category: "Raw Material", unit: "Kg", hsn: "7216", gstRate: 18, openingStock: 600, currentStock: 480, minStock: 100, reorderLevel: 200, purchaseRate: 70, saleRate: 85 },
      { id: "i8", code: "RM-COPFLAT", name: "Copper Flat", category: "Raw Material", unit: "Kg", hsn: "7409", gstRate: 18, openingStock: 200, currentStock: 140, minStock: 50, reorderLevel: 80, purchaseRate: 950, saleRate: 1050 },
      { id: "i9", code: "RM-HVBSH", name: "HV Bushing", category: "Raw Material", unit: "Nos", hsn: "8546", gstRate: 18, openingStock: 200, currentStock: 150, minStock: 30, reorderLevel: 60, purchaseRate: 250, saleRate: 290 },
      { id: "i10", code: "RM-LVBSH", name: "LV Bushing", category: "Raw Material", unit: "Nos", hsn: "8546", gstRate: 18, openingStock: 250, currentStock: 180, minStock: 40, reorderLevel: 80, purchaseRate: 25, saleRate: 35 },
      { id: "i11", code: "FG-TXR-100", name: "100 KVA Transformer", category: "Finished Goods", unit: "Nos", hsn: "8504", gstRate: 18, openingStock: 0, currentStock: 4, minStock: 0, reorderLevel: 0, purchaseRate: 0, saleRate: 185000 },
      { id: "i12", code: "FG-TXR-250", name: "250 KVA Transformer", category: "Finished Goods", unit: "Nos", hsn: "8504", gstRate: 18, openingStock: 0, currentStock: 2, minStock: 0, reorderLevel: 0, purchaseRate: 0, saleRate: 320000 },
    ],
    costings: [
      {
        id: "c1", number: "CS-2026-001", title: "100 KVA Distribution Transformer", customerId: "p1", kva: "100",
        materials: [
          { name: "CRGO", qty: 66.47, rate: 200 },
          { name: "LV Conductor", qty: 16.183, rate: 280 },
          { name: "HV Conductor", qty: 14.590, rate: 280 },
          { name: "Transformer Oil", qty: 86, rate: 75 },
          { name: "Tank", qty: 109, rate: 75 },
          { name: "PSR Radiator", qty: 9, rate: 110 },
          { name: "Core Channel Weight", qty: 24.25, rate: 70 },
          { name: "Copper Flat", qty: 5, rate: 950 },
          { name: "HV Bushing", qty: 3, rate: 250 },
          { name: "LV Bushing", qty: 4, rate: 25 },
          { name: "LV & HV Metal Set", qty: 1, rate: 2500 },
          { name: "Off Load Tap Changer", qty: 1, rate: 3500 },
          { name: "Pressure Relief Valve", qty: 1, rate: 2000 },
          { name: "Labour", qty: 1, rate: 7500 },
        ],
        gstRate: 18, marginPct: 25, status: "approved", ownerId: "u-sales1", createdAt: today, version: 1,
      },
    ],
    quotations: [
      {
        id: "q1", number: "QTN-2026-001", date: today.slice(0,10), customerId: "p1", costingId: "c1",
        items: [{ name: "100 KVA Transformer", qty: 2, rate: 185000, gst: 18 }],
        terms: "Delivery within 30 days. Payment: 50% advance, 50% before dispatch.",
        status: "Quotation Sent", ownerId: "u-sales1", createdAt: today,
      },
    ],
    proformas: [],
    salesOrders: [],
    purchaseOrders: [
      {
        id: "po1", number: "PO-2026-001", date: today.slice(0,10), vendorId: "p3",
        items: [{ itemId: "i1", qty: 1000, rate: 200 }, { itemId: "i4", qty: 500, rate: 75 }],
        status: "Approved", createdAt: today,
      },
    ],
    grns: [],
    grnAudit: [],
    materialIssues: [],
    boms: [
      {
        id: "b1", name: "100 KVA Transformer BOM", kva: "100",
        materials: [
          { itemId: "i1", name: "CRGO Steel", qty: 66.47, unit: "Kg" },
          { itemId: "i2", name: "LV Conductor", qty: 16.183, unit: "Kg" },
          { itemId: "i3", name: "HV Conductor", qty: 14.590, unit: "Kg" },
          { itemId: "i4", name: "Transformer Oil", qty: 86, unit: "Ltr" },
          { itemId: "i5", name: "MS Tank", qty: 109, unit: "Kg" },
          { itemId: "i6", name: "PSR Radiator", qty: 9, unit: "Nos" },
          { itemId: "i7", name: "Core Channel", qty: 24.25, unit: "Kg" },
          { itemId: "i8", name: "Copper Flat", qty: 5, unit: "Kg" },
          { itemId: "i9", name: "HV Bushing", qty: 3, unit: "Nos" },
          { itemId: "i10", name: "LV Bushing", qty: 4, unit: "Nos" },
        ],
        createdAt: today,
      },
    ],
    jobCards: [],
    challans: [],
    productionEntries: [],
    sfgBatches: [],
    sfgConsumptions: [],
    operators: [],
    serials: [],
    qcTests: [],
    qcFinalReports: [],
    qcFormats: [
      { id: "qcf-dtr", name: "DTR Routine Test Format", useFor: "Distribution Transformer", pdfLayout: "routine", createdAt: today, columns: [
        { id: "ambTemp", name: "Amb. Temp in Deg. C", width: 90, parameter: "Ambient Temperature" },
        { id: "nllFreq", name: "Freq. (Hz)", width: 80, parameter: "No Load Losses (Watts) and Currents (Amp)" },
        { id: "nllVoltage", name: "Voltage (Volts)", width: 90, parameter: "No Load Losses (Watts) and Currents (Amp)" },
        { id: "nllCurrent100", name: "I (Amp) at 100% Volts", width: 110, parameter: "No Load Losses (Watts) and Currents (Amp)" },
        { id: "nllMeasuredWatts", name: "Measured NLL (Watts)", width: 110, parameter: "No Load Losses (Watts) and Currents (Amp)" },
        { id: "ll50Freq", name: "Freq. (Hz)", width: 80, parameter: "Load Losses at 50%" },
        { id: "ll50Voltage", name: "Voltage (Volts)", width: 90, parameter: "Load Losses at 50%" },
        { id: "ll50AppliedCurrent", name: "Applied Currents (Amp.)", width: 110, parameter: "Load Losses at 50%" },
        { id: "ll50MeasuredLossAmb", name: "Measured Loss at Amb. Temp.", width: 120, parameter: "Load Losses at 50%" },
        { id: "ll50TotalLoss", name: "Total Losses at 50%", width: 110, parameter: "Load Losses at 50%" },
        { id: "ll100Freq", name: "Freq. (Hz)", width: 80, parameter: "Load Losses at 100%" },
        { id: "ll100ImpVoltage", name: "Imp. Voltage (Volts)", width: 110, parameter: "Load Losses at 100%" },
        { id: "ll100AppliedCurrent", name: "Applied Currents (Amp.)", width: 110, parameter: "Load Losses at 100%" },
        { id: "ll100MeasuredLossAmb", name: "Measured Loss at Amb. Temp.", width: 120, parameter: "Load Losses at 100%" },
        { id: "ll100TotalLoss", name: "Total Losses at 100%", width: 110, parameter: "Load Losses at 100%" },
        { id: "source28kv", name: "Separate Source test at 28 kV for HV Side for 60 Sec.", width: 120, parameter: "Separate Source Test", passFailLogic: "Pass" },
        { id: "source3kv", name: "Separate Source test at 3 kV for LV Side for 60 Sec.", width: 120, parameter: "Separate Source Test", passFailLogic: "Pass" },
        { id: "dvdf866", name: "DVDF test at 866 Volts from LV Side at 100 Hz for 60 Sec.", width: 130, parameter: "DVDF", passFailLogic: "Pass" },
      ]},
      { id: "qcf-ct", name: "CT Routine Test Format", useFor: "Current Transformer", pdfLayout: "routine", createdAt: today, columns: [
        { id: "ctr", name: "CTR", width: 90, parameter: "CTR" },
        { id: "polarity", name: "Polarity", width: 90, parameter: "Polarity" },
        { id: "sepHv", name: "Separate source test at HV Side for 60 sec.", width: 110, passFailLogic: "28 kV" },
        { id: "sepLv", name: "Separate source test at LV Side for 60 sec.", width: 110, passFailLogic: "3 kV" },
        { id: "ir", name: "Insulation Resistance", width: 110, parameter: "ELT-ELT, HT-HT-E" },
        { id: "b10_120", name: "120%", width: 80, parameter: "Burden 10 VA" },
        { id: "b10_20", name: "20%", width: 80, parameter: "Burden 10 VA" },
        { id: "b10_5", name: "5%", width: 80, parameter: "Burden 10 VA" },
        { id: "b10_1", name: "1%", width: 80, parameter: "Burden 10 VA" },
        { id: "b25_120", name: "120%", width: 80, parameter: "Burden 2.5 VA" },
        { id: "b25_20", name: "20%", width: 80, parameter: "Burden 2.5 VA" },
        { id: "b25_5", name: "5%", width: 80, parameter: "Burden 2.5 VA" },
        { id: "b25_1", name: "1%", width: 80, parameter: "Burden 2.5 VA" },
      ]},
      { id: "qcf-pt", name: "PT Routine Test Format", useFor: "Potential Transformer", pdfLayout: "routine", createdAt: today, columns: [
        { id: "ptType", name: "Type", width: 160, parameter: "11 KV Resin Cast PT-3Ph" },
        { id: "polarity", name: "Polarity", width: 90 },
        { id: "sepLv03", name: "Separate source test at 03 KV for LV Side for 60 sec.", width: 120, passFailLogic: "3 kV" },
        { id: "dvdf", name: "Induced Over Voltage (DVDF)", width: 110, passFailLogic: "280 V" },
        { id: "sepHv", name: "Separate source test at for HV Side for 60 sec", width: 120, passFailLogic: "28 kV" },
        { id: "ir", name: "Insulation Resistance", width: 110, parameter: "LT-E,LT-HT,HT-E" },
        { id: "ab_120_ratio", name: "A-B 120% Ratio Error", width: 110, parameter: "Accuracy Class 0.5" },
        { id: "ab_120_phase", name: "A-B 120% Phase Error", width: 110, parameter: "Accuracy Class 0.5" },
        { id: "ab_80_ratio", name: "A-B 80% Ratio Error", width: 110, parameter: "Accuracy Class 0.5" },
        { id: "ab_80_phase", name: "A-B 80% Phase Error", width: 110, parameter: "Accuracy Class 0.5" },
        { id: "ac_120_ratio", name: "A-C 120% Ratio Error", width: 110, parameter: "Accuracy Class 0.5" },
        { id: "ac_120_phase", name: "A-C 120% Phase Error", width: 110, parameter: "Accuracy Class 0.5" },
        { id: "ac_80_ratio", name: "A-C 80% Ratio Error", width: 110, parameter: "Accuracy Class 0.5" },
        { id: "ac_80_phase", name: "A-C 80% Phase Error", width: 110, parameter: "Accuracy Class 0.5" },
        { id: "bc_120_ratio", name: "B-C 120% Ratio Error", width: 110, parameter: "Accuracy Class 0.5" },
        { id: "bc_120_phase", name: "B-C 120% Phase Error", width: 110, parameter: "Accuracy Class 0.5" },
        { id: "bc_80_ratio", name: "B-C 80% Ratio Error", width: 110, parameter: "Accuracy Class 0.5" },
        { id: "bc_80_phase", name: "B-C 80% Phase Error", width: 110, parameter: "Accuracy Class 0.5" },
        { id: "conforms", name: "Conforms / Does not Conforms", width: 130 },
        { id: "remarks", name: "Remarks if Any", width: 140 },
      ]},
      { id: "qcf-oil", name: "CT PT Metering Unit Test Format", useFor: "CT PT Metering Unit", pdfLayout: "routine", createdAt: today, columns: [
        { id: "ctType", name: "Type", width: 140, parameter: "CT/PT Type" },
        { id: "classVa", name: "Class / VA", width: 100, parameter: "Class / VA" },
        { id: "ilKv", name: "I.L. kVp", width: 100, parameter: "Impulse Level" },
        { id: "sepHv", name: "Separate source test at HV Side for 60 sec.", width: 110, passFailLogic: "28 kV" },
        { id: "sepLv", name: "Separate source test at LV Side for 60 sec.", width: 110, passFailLogic: "3 kV" },
        { id: "dvdf", name: "Induced Over Voltage (DVDF)", width: 110, passFailLogic: "280 V" },
        { id: "ir", name: "Insulation Resistance", width: 110, parameter: "ELT-ELT, HT-HT-E" },
        { id: "conforms", name: "Conforms / Does not Conforms", width: 120 },
        { id: "remarks", name: "Remarks if Any", width: 140 },
      ]},
      { id: "qcf-final", name: "Final Inspection Format", useFor: "Dispatch QC", pdfLayout: "certificate", createdAt: today, columns: [
        { id: "visual", name: "Visual Check", width: 140 }, { id: "nameplate", name: "Nameplate", width: 120 }, { id: "packing", name: "Packing", width: 120 },
      ]},
    ],
    leads: [],
    ctCostings: [],
    logs: [],
    settings: {
      name: "AMREST ELECTRICALS LIMITED",
      address: "Plot No. 42, Industrial Area, Jaipur, Rajasthan 302013",
      gst: "08AAGCA1234L1ZP",
      email: "info@amrest.in",
      phone: "+91 98765 43210",
      logoText: "AE",
      invoicePrefix: "AE",
      fyStart: "2026-04-01",
      documentFormats: defaultDocumentFormats(today),
    },
  };
}

export { defaultDocumentFormats };
